/**
 * モノクロ変換 (L2: フィルム特性反映)
 *
 * 各フィルムの分光感度を RGB チャンネル重みで近似し、
 * フィルター効果は重みの再調整で表現する。
 *
 * Canvas 2D の getImageData で全ピクセルを走査するため、
 * 入力画像は事前に縮小しておく（プレビュー用は 1024px 上限推奨）。
 */

import { FILTER_FACTORS } from './presets.js';
import { getFilmById } from './data/films.js';

/**
 * フィルム別 RGB 重み（合計 1.0）
 * Wright/Wikipedia パンクロフィルム分光感度カーブを近似
 */
export const FILM_WEIGHTS = {
  panchromatic: { r: 0.30, g: 0.59, b: 0.11 },  // ITU-R BT.601 標準
  'tri-x':      { r: 0.36, g: 0.50, b: 0.14 },  // やや赤に敏感
  tmax:         { r: 0.30, g: 0.55, b: 0.15 },  // 現代パンクロ、ニュートラル
  hp5:          { r: 0.32, g: 0.54, b: 0.14 },  // 中庸
  acros:        { r: 0.27, g: 0.55, b: 0.18 },  // 滑らか、青に少し敏感
  sfx:          { r: 0.50, g: 0.30, b: 0.20 },  // 近赤外感度（赤強い）
};

/**
 * フィルター効果による重み変調
 * - フィルターが通す色のチャンネルを強調、補色を減衰
 * - filmWeights * (1 - filterIntensity) + filterTint * filterIntensity の線形補間
 *
 * filter intensity は実機フィルムの効果に近い値で調整。
 */
export const FILTER_TINTS = {
  '':         { r: 0,    g: 0,    b: 0,    intensity: 0,    contrast: 0 },
  yellow:     { r: 0.42, g: 0.50, b: 0.08, intensity: 0.55, contrast: 0.05 },
  orange:     { r: 0.58, g: 0.36, b: 0.06, intensity: 0.70, contrast: 0.10 },
  red:        { r: 0.85, g: 0.12, b: 0.03, intensity: 0.85, contrast: 0.20 },
  green:      { r: 0.12, g: 0.62, b: 0.26, intensity: 0.65, contrast: 0.05 },
  polarizer:  { r: 0,    g: 0,    b: 0,    intensity: 0,    contrast: 0.15 },
};

const FILTER_KEY_MAP = {
  '':           '',
  'yellow8':    'yellow',
  'orange16':   'orange',
  'red25':      'red',
  'green11':    'green',
  'polarizer':  'polarizer',
};

/**
 * フィルム + フィルターから最終重みを計算
 */
export function computeWeights(filmMonoPreset, filterId) {
  const baseWeight = FILM_WEIGHTS[filmMonoPreset] || FILM_WEIGHTS.panchromatic;
  const filterKey = FILTER_KEY_MAP[filterId] ?? '';
  const filter = FILTER_TINTS[filterKey];

  if (!filter || filter.intensity === 0) {
    return { ...baseWeight, contrast: filter?.contrast ?? 0 };
  }

  // 線形補間
  const t = filter.intensity;
  const w = {
    r: baseWeight.r * (1 - t) + filter.r * t,
    g: baseWeight.g * (1 - t) + filter.g * t,
    b: baseWeight.b * (1 - t) + filter.b * t,
  };
  // 正規化（合計を 1.0 に）
  const sum = w.r + w.g + w.b;
  return {
    r: w.r / sum,
    g: w.g / sum,
    b: w.b / sum,
    contrast: filter.contrast,
  };
}

/**
 * プリセットからの便宜関数
 */
export function computeWeightsFromPreset(preset) {
  const film = getFilmById(preset.filmId);
  const monoPreset = film?.monoPreset || 'panchromatic';
  return computeWeights(monoPreset, preset.filterId);
}

/**
 * Canvas にモノクロ変換を適用
 *
 * @param {HTMLCanvasElement | OffscreenCanvas} sourceCanvas - 元画像が描画された canvas
 * @param {HTMLCanvasElement} destCanvas - 出力先（同サイズ）
 * @param {{r: number, g: number, b: number, contrast?: number}} weights
 */
export function applyMonoToCanvas(sourceCanvas, destCanvas, weights) {
  const w = sourceCanvas.width;
  const h = sourceCanvas.height;
  destCanvas.width = w;
  destCanvas.height = h;

  const sCtx = sourceCanvas.getContext('2d');
  const dCtx = destCanvas.getContext('2d');
  const src = sCtx.getImageData(0, 0, w, h);
  const dst = dCtx.createImageData(w, h);

  const data = src.data;
  const out = dst.data;
  const wr = weights.r;
  const wg = weights.g;
  const wb = weights.b;
  const contrastBoost = weights.contrast || 0;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    let y = wr * r + wg * g + wb * b;
    if (contrastBoost) {
      // 中央 128 を中心にコントラストを上げる
      y = 128 + (y - 128) * (1 + contrastBoost);
    }
    y = y < 0 ? 0 : y > 255 ? 255 : Math.round(y);
    out[i] = y;
    out[i + 1] = y;
    out[i + 2] = y;
    out[i + 3] = data[i + 3];
  }
  dCtx.putImageData(dst, 0, 0);
}

/**
 * Blob からモノクロ Blob を生成
 *
 * @param {Blob} blob
 * @param {object} weights
 * @param {number} [maxDim=1600] - リサイズ最大辺（パフォーマンス確保）
 * @returns {Promise<{ blob: Blob, width: number, height: number }>}
 */
export async function blobToMonoBlob(blob, weights, maxDim = 1600) {
  const img = await blobToImage(blob);

  // 縮小
  let w = img.naturalWidth;
  let h = img.naturalHeight;
  const maxSide = Math.max(w, h);
  if (maxSide > maxDim) {
    const scale = maxDim / maxSide;
    w = Math.round(w * scale);
    h = Math.round(h * scale);
  }

  const src = document.createElement('canvas');
  src.width = w;
  src.height = h;
  const sCtx = src.getContext('2d');
  sCtx.imageSmoothingQuality = 'high';
  sCtx.drawImage(img, 0, 0, w, h);

  const dst = document.createElement('canvas');
  applyMonoToCanvas(src, dst, weights);

  return new Promise((resolve) => {
    dst.toBlob((b) => resolve({ blob: b, width: w, height: h }), 'image/jpeg', 0.86);
  });
}

function blobToImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

/**
 * Canvas に直接 Blob から描画してモノクロ化（プレビュー用）
 */
export async function drawMonoToCanvas(blob, destCanvas, weights, maxDim = 1024) {
  const img = await blobToImage(blob);
  let w = img.naturalWidth;
  let h = img.naturalHeight;
  const maxSide = Math.max(w, h);
  if (maxSide > maxDim) {
    const scale = maxDim / maxSide;
    w = Math.round(w * scale);
    h = Math.round(h * scale);
  }
  const tmp = document.createElement('canvas');
  tmp.width = w;
  tmp.height = h;
  tmp.getContext('2d').drawImage(img, 0, 0, w, h);
  applyMonoToCanvas(tmp, destCanvas, weights);
  return { width: w, height: h };
}
